import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Skeleton,
  Alert,
} from '@mui/material';
import BackIcon from '@mui/icons-material/ArrowBack';

import MainCard from '../../components/MainCard';
import CustomButton from '../../components/@extended/CustomButton';
import {
  useGetSupportTicketByIdQuery,
  useAddSupportTicketReplyMutation,
  useUpdateSupportTicketStatusMutation,
} from '../../store/slices/apiSlice';
import { useDispatch } from 'react-redux';
import { openMessage } from '../../store/slices/showMessageSlice';
import { STATUS_OPTIONS, getStatusConfig, REPLY_MAX_LENGTH } from '../../util/supportTicketConstants';

const StatusChip = ({ status }) => {
  const { label, color, text } = getStatusConfig(status);
  return (
    <Chip
      label={label}
      size="small"
      sx={{ backgroundColor: color, color: text, fontWeight: 500, fontSize: '0.75rem' }}
    />
  );
};

const SupportTicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [replyText, setReplyText] = useState('');

  const { data: ticket, isLoading, error } = useGetSupportTicketByIdQuery(id, { skip: !id });
  const [addReply, { isLoading: isSubmittingReply }] = useAddSupportTicketReplyMutation();
  const [updateStatus, { isLoading: isUpdatingStatus }] = useUpdateSupportTicketStatusMutation();

  const handleBack = () => navigate('/admin/support-tickets');

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    try {
      await updateStatus({ id, status: newStatus }).unwrap();
      dispatch(openMessage({ message: 'Status updated.', messageSeverity: 'success' }));
    } catch (err) {
      dispatch(
        openMessage({
          message: err?.data?.message || 'Failed to update status.',
          messageSeverity: 'error',
        }),
      );
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    const msg = replyText?.trim();
    if (!msg) return;
    try {
      await addReply({ id, message: msg }).unwrap();
      setReplyText('');
      dispatch(openMessage({ message: 'Reply added.', messageSeverity: 'success' }));
    } catch (err) {
      dispatch(
        openMessage({
          message: err?.data?.message || 'Failed to add reply.',
          messageSeverity: 'error',
        }),
      );
    }
  };

  if (!id) {
    return (
      <MainCard title="Support Ticket">
        <Alert severity="error">Invalid ticket ID.</Alert>
        <Button startIcon={<BackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to list
        </Button>
      </MainCard>
    );
  }

  if (error) {
    return (
      <MainCard title="Support Ticket">
        <Alert severity="error">Failed to load ticket. It may not exist.</Alert>
        <Button startIcon={<BackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to list
        </Button>
      </MainCard>
    );
  }

  if (isLoading || !ticket) {
    return (
      <MainCard title="Support Ticket">
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
      </MainCard>
    );
  }

  const replies = ticket.replies ?? [];

  return (
    <MainCard
      title={
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button startIcon={<BackIcon />} onClick={handleBack} size="small" sx={{ mr: 1 }}>
            Back
          </Button>
          <Typography variant="h5">Support Ticket</Typography>
          <StatusChip status={ticket.status} />
        </Stack>
      }
    >
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          User name
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {ticket.userName}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Email
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {ticket.email}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Organization
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {ticket.organizationName}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Problem type
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {ticket.problemType || '–'}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Description
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          {ticket.description}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Created {ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : '–'}
        </Typography>
      </Paper>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Status
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={ticket.status}
            onChange={handleStatusChange}
            label="Status"
            disabled={isUpdatingStatus}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Replies ({replies.length})
      </Typography>
      {replies.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No replies yet.
        </Typography>
      ) : (
        <Stack spacing={2} sx={{ mb: 3 }}>
          {replies.map((r) => (
            <Paper key={r.id} variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {r.createdBy} · {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {r.message}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Add reply
      </Typography>
      <form onSubmit={handleSubmitReply}>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Type your reply..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          helperText={`${(replyText ?? '').length} / ${REPLY_MAX_LENGTH}`}
          inputProps={{ maxLength: REPLY_MAX_LENGTH }}
          sx={{ mb: 2 }}
        />
        <CustomButton type="submit" disabled={!replyText?.trim() || isSubmittingReply}>
          {isSubmittingReply ? 'Sending...' : 'Send reply'}
        </CustomButton>
      </form>
    </MainCard>
  );
};

export default SupportTicketDetail;
